// redux/slices/favoritesSlice.js

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import axios from 'axios';

const baseUrl = process.env.REACT_APP_API_BASE_URL;

// Thunk: Get all favorites
export const fetchFavorites = createAsyncThunk(
    'favorites/fetchFavorites',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${baseUrl}/favorites`);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error fetching favorites');
        }
    }
);

// Thunk: Toggle favorite
export const toggleFavorite = createAsyncThunk(
  'favorites/toggleFavorite',
  async (product_id, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const user_id = state.auth.user?.id;
      if (!user_id) return rejectWithValue("User not authenticated");

      const existingFavorite = state.favorites.items.find(
        (fav) => fav.product_id === product_id
      );

      if (existingFavorite) {
        // If already favorite → delete
        await axios.delete(`${baseUrl}/favorites/${existingFavorite.id}`);
      } else {
        // If not → add
        await axios.post(`${baseUrl}/favorites`, {
          user_id,
          product_id,
        });
      }

      // Refresh favorites
      await dispatch(fetchFavorites());

      return product_id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error toggling favorite");
    }
  }
);

// Thunk: Delete favorite by ID
export const deleteFavorite = createAsyncThunk(
    'favorites/deleteFavorite',
    async (favoriteId, { rejectWithValue }) => {
        try {
            await axios.delete(`${baseUrl}/favorites/${favoriteId}`);
            return favoriteId;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error deleting favorite');
        }
    }
);

const favoritesSlice = createSlice({
    name: 'favorites',
    initialState: {
        items: [],
        loading: false,
        error: null,
        clearing: false,
        togglingProductId: null,
    },
    reducers: {
        clearFavorites: (state) => {
            state.items = [];
            state.clearing = false;
        },
        startClearingFavorites: (state) => {
            state.clearing = true;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch
            .addCase(fetchFavorites.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFavorites.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload;
            })
            .addCase(fetchFavorites.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Toggle
            .addCase(toggleFavorite.pending, (state, action) => {
                state.togglingProductId = action.meta.arg;
            })
            .addCase(toggleFavorite.fulfilled, (state) => {
                state.togglingProductId = null;
            })
            .addCase(toggleFavorite.rejected, (state) => {
                state.togglingProductId = null;
            })

            // Delete
            .addCase(deleteFavorite.fulfilled, (state, action) => {
                state.items = state.items.filter((fav) => fav.id !== action.payload);
            });
    },
});

export const { clearFavorites, startClearingFavorites } = favoritesSlice.actions;

export const selectFavorites = (state) =>
    Array.isArray(state.favorites.items) ? state.favorites.items : [];

export const selectFavoritesLoading = (state) => state.favorites.loading;
export const selectFavoritesClearing = (state) => state.favorites.clearing;
export const selectFavoritesError = (state) => state.favorites.error;
export const selectTogglingProductId = (state) => state.favorites.togglingProductId;

export default favoritesSlice.reducer;
